import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { InfoService } from '@wonder-core-ng/info-store/info.service';
import { selectSelectedTenantId } from '@wonder-core-ng/tenant-store/tenant.selectors';
import { isNotNull } from '@wonder-core-ng/utils';
import { catchError, concatMap, filter, map, of } from 'rxjs';
import { ConditionActions } from './condition.actions';
import { ConditionApiService } from './condition.api.service';

@Injectable()
export class ConditionEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(ConditionApiService);
  private readonly store = inject(Store);
  private readonly infoService = inject(InfoService);

  private readonly tenantId$ = () =>
    this.store.select(selectSelectedTenantId).pipe(filter(isNotNull));

  loadConditions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConditionActions.loadConditions),
      concatLatestFrom(this.tenantId$),
      concatMap(([, tenantId]) =>
        this.api.getAllConditions(tenantId).pipe(
          map((conditions) => ConditionActions.loadConditionsSuccess({ conditions })),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$condition.error-loading',
              severity: 'error',
              error,
            });
            return of(ConditionActions.loadConditionsFailure({ error }));
          })
        )
      )
    )
  );

  createCondition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConditionActions.createCondition),
      concatLatestFrom(this.tenantId$),
      concatMap(([{ dto }, tenantId]) =>
        this.api.createCondition(dto, tenantId).pipe(
          map((condition) => {
            this.infoService.addMessage({ message: '$condition.saved', severity: 'success' });
            return ConditionActions.createConditionSuccess({ condition });
          }),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$condition.error-saving',
              severity: 'error',
              error,
            });
            return of(ConditionActions.createConditionFailure({ error }));
          })
        )
      )
    )
  );

  updateCondition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConditionActions.updateCondition),
      concatLatestFrom(this.tenantId$),
      concatMap(([{ id, dto }, tenantId]) =>
        this.api.updateCondition(id, dto, tenantId).pipe(
          map((condition) => {
            this.infoService.addMessage({ message: '$condition.saved', severity: 'success' });
            return ConditionActions.updateConditionSuccess({ condition });
          }),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$condition.error-saving',
              severity: 'error',
              error,
            });
            return of(ConditionActions.updateConditionFailure({ error }));
          })
        )
      )
    )
  );

  deleteCondition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConditionActions.deleteCondition),
      concatLatestFrom(this.tenantId$),
      concatMap(([{ id }, tenantId]) =>
        this.api.deleteCondition(id, tenantId).pipe(
          map(() => ConditionActions.deleteConditionSuccess({ id })),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$condition.error-deleting',
              severity: 'error',
              error,
            });
            return of(ConditionActions.deleteConditionFailure({ error }));
          })
        )
      )
    )
  );
}
